/**
 * Currency formatting utility
 * Provides flexible currency formatting with configurable options
 */
// Default configuration for currencies
const DEFAULT_CURRENCY_CONFIG = {
  RUB: {
    symbol: '₽',
    position: 'right',
    spacing: true,
    decimalPlaces: 0,
    decimalSeparator: ',',
    thousandsSeparator: ' ',
  },
  USD: {
    symbol: '$',
    position: 'left',
    spacing: false,
    decimalPlaces: 2,
    decimalSeparator: '.',
    thousandsSeparator: ',',
  },
  EUR: {
    symbol: '€',
    position: 'right',
    spacing: true,
    decimalPlaces: 2,
    decimalSeparator: ',',
    thousandsSeparator: ' ',
    locales: {
      en: {
        symbol: '€',
        position: 'left',
        spacing: false,
        decimalSeparator: '.',
        thousandsSeparator: ',',
      },
    },
  },
  ILS: {
    symbol: '₪',
    position: 'right',
    spacing: true,
    decimalPlaces: 0,
    decimalSeparator: '.',
    thousandsSeparator: ',',
  },
  BYN: {
    symbol: 'BYN',
    position: 'right',
    spacing: true,
    decimalPlaces: 2,
    decimalSeparator: ',',
    thousandsSeparator: ' ',
    locales: {
      ru: { symbol: 'руб' },
    },
  },
  THB: {
    symbol: '฿',
    position: 'right',
    spacing: true,
    decimalPlaces: 0,
    decimalSeparator: '.',
    thousandsSeparator: ',',
  },
  CZK: {
    symbol: 'Kč',
    position: 'right',
    spacing: true,
    decimalPlaces: 0,
    decimalSeparator: ',',
    thousandsSeparator: ' ',
  },
  GBP: {
    symbol: '£',
    position: 'left',
    spacing: false,
    decimalPlaces: 2,
    decimalSeparator: '.',
    thousandsSeparator: ',',
  },
  AED: {
    symbol: 'AED',
    position: 'right',
    spacing: true,
    decimalPlaces: 0,
    decimalSeparator: '.',
    thousandsSeparator: ',',
  },
  UZS: {
    symbol: 'Soʻm',
    position: 'right',
    spacing: true,
    decimalPlaces: 0,
    decimalSeparator: ',',
    thousandsSeparator: ' ',
  },
};

const DEFAULT_NUMBER_CONFIG = {
  decimalPlaces: 2,
  decimalSeparator: ',',
  thousandsSeparator: ' ',
};

/**
 * Format a value as currency with compact notation (e.g., 1.5M, 2K)
 * @param {number} value - The value to format
 * @param {string} currencyCode - Currency code
 * @param {string} locale - Locale code
 * @returns {string} - Formatted compact currency string
 */

// Default compact number symbols configuration
const DEFAULT_COMPACT_SYMBOLS = {
  '': '',
  k: 'k',
  M: 'M',
  G: 'G',
  T: 'T',
  P: 'P',
  E: 'E',
};

// Locale-specific compact number symbols
let locale_compact_symbols = DEFAULT_COMPACT_SYMBOLS;

/**
 * Set the locale for compact number symbols
 * @param {object} locale - Locale object
 */
export function setCompactSymbolsLocale(locale) {
  locale_compact_symbols = locale;
}

/**
 * Format a number with specified decimal and thousands separators
 * @param {number} value - The number to format
 * @param {number} decimalPlaces - Number of decimal places
 * @param {string} decimalSeparator - Character to use as decimal separator
 * @param {string} thousandsSeparator - Character to use as thousands separator
 * @returns {string} - Formatted number
 */
export function formatNumber(
  value,
  decimalPlaces = null,
  decimalSeparator = null,
  thousandsSeparator = null
) {
  if (decimalPlaces == null) {
    decimalPlaces = DEFAULT_NUMBER_CONFIG.decimalPlaces;
  }
  if (decimalSeparator == null) {
    decimalSeparator = DEFAULT_NUMBER_CONFIG.decimalSeparator;
  }
  if (thousandsSeparator == null) {
    thousandsSeparator = DEFAULT_NUMBER_CONFIG.thousandsSeparator;
  }
  if (value === null || value === undefined) return '';

  // Convert to fixed decimal places
  const fixedValue = value.toFixed(decimalPlaces);

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = fixedValue.split('.');

  // Format integer part with thousands separator
  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousandsSeparator
  );

  // Handle decimal part - remove trailing zeros
  if (decimalPart !== undefined) {
    // Remove trailing zeros
    const cleanDecimalPart = decimalPart.replace(/0+$/, '');

    // If there are significant digits left in the decimal part, include them
    if (cleanDecimalPart.length > 0) {
      return `${formattedIntegerPart}${decimalSeparator}${cleanDecimalPart}`;
    }
  }

  // Return just the integer part if no significant decimal digits
  return formattedIntegerPart;
}

export function formatCompactNumber(value) {
  const formatInfo = getNumberFormat(value);

  // Format with compact notation
  let compactValue;
  if (value >= 1000) {
    const formatted = value / formatInfo.value;
    // const numericPart = formatted.endsWith('.0')
    //   ? formatted.slice(0, -2)
    //   : formatted;

    const formattedNumber = formatNumber(formatted, 0, '.', ' ');

    compactValue = `${formattedNumber}${formatInfo.locale}`;
  } else {
    compactValue = value.toString();
  }
  return compactValue;
}

/**
 * Format a value as currency
 * @param {number} value - The value to format
 * @param {string} currencyCode - Currency code (e.g., 'USD', 'RUB')
 * @param {string} locale - Locale code (e.g., 'en', 'ru')
 * @param {Object} customConfig - Optional custom configuration to override defaults
 * @returns {string} - Formatted currency string
 */
export function formatCurrency(
  value,
  currencyCode,
  locale = 'en',
  customConfig = {}
) {
  if (value === null || value === undefined) return '';

  // Get currency configuration, with fallbacks
  const defaultConfig = DEFAULT_CURRENCY_CONFIG[currencyCode] || {
    symbol: currencyCode,
    position: 'right',
    spacing: true,
    decimalPlaces: 0,
    decimalSeparator: ',',
    thousandsSeparator: ' ',
  };

  // Get locale-specific overrides
  const localeConfig = defaultConfig.locales?.[locale] || {};

  // Merge configurations with priority: customConfig > localeConfig > defaultConfig
  const config = {
    ...defaultConfig,
    ...localeConfig,
    ...customConfig,
  };

  const {
    symbol,
    position,
    spacing,
    decimalPlaces,
    decimalSeparator,
    thousandsSeparator,
  } = config;

  // Format the number part
  const formattedNumber = formatNumber(
    value,
    decimalPlaces,
    decimalSeparator,
    thousandsSeparator
  );

  // Add symbol based on position
  if (position === 'left') {
    return spacing
      ? `${symbol} ${formattedNumber}`
      : `${symbol}${formattedNumber}`;
  } else {
    return spacing
      ? `${formattedNumber} ${symbol}`
      : `${formattedNumber}${symbol}`;
  }
}

/**
 * Get number format information for compact display
 * @param {number} num - The number to analyze
 * @returns {Object} - Format information with value and symbol
 */
export function getNumberFormat(num) {
  const priceFormatTypes = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ];

  const item = priceFormatTypes
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    }) || { value: 1, symbol: '', locale: '' };

  if (item && item.symbol) {
    item.locale = locale_compact_symbols[item.symbol];
  }

  return item;
}

/**
 * Format a value as currency with compact notation (e.g., 1.5M, 2K)
 * @param {number} value - The value to format
 * @param {string} currencyCode - Currency code
 * @param {string} locale - Locale code
 * @returns {string} - Formatted compact currency string
 */
export function formatCompactCurrency(value, currencyCode, locale = 'en') {
  if (value === null || value === undefined) return '';

  const config = DEFAULT_CURRENCY_CONFIG[currencyCode] || {
    symbol: currencyCode,
    position: 'right',
    spacing: true,
    decimalPlaces: 0,
    decimalSeparator: ',',
    thousandsSeparator: ' ',
  };
  const localeConfig = config.locales?.[locale] || {};
  const symbol = localeConfig.symbol || config.symbol;
  const position = localeConfig.position || config.position;
  const spacing =
    localeConfig.spacing !== undefined ? localeConfig.spacing : config.spacing;

  const compactValue = formatCompactNumber(value);
  // Add symbol based on position
  if (position === 'left') {
    return spacing ? `${symbol} ${compactValue}` : `${symbol}${compactValue}`;
  } else {
    return spacing ? `${compactValue} ${symbol}` : `${compactValue}${symbol}`;
  }
}

/**
 * Get compact symbol for the current locale
 * @param {string} symbol - Base symbol (k, M, etc.)
 * @returns {string} - Localized compact symbol
 */
export function getCompactSymbol(symbol) {
  return (
    locale_compact_symbols[symbol] || DEFAULT_COMPACT_SYMBOLS[symbol] || symbol
  );
}

export function getCurrencySymbol(currencyCode, locale = 'en') {
  const config = DEFAULT_CURRENCY_CONFIG[currencyCode] || {
    symbol: currencyCode,
    position: 'right',
    spacing: true,
    decimalPlaces: 0,
    decimalSeparator: ',',
    thousandsSeparator: ' ',
  };
  const localeConfig = config.locales?.[locale] || {};
  return localeConfig.symbol || config.symbol;
}

/**
 * Add a new currency configuration or update an existing one
 * @param {string} currencyCode - Currency code to add/update
 * @param {Object} config - Currency configuration
 */
export function configureCurrency(currencyCode, config) {
  DEFAULT_CURRENCY_CONFIG[currencyCode] = {
    ...DEFAULT_CURRENCY_CONFIG[currencyCode],
    ...config,
  };
}

export default {
  setCompactSymbolsLocale,
  formatNumber,
  formatCompactNumber,
  formatCurrency,
  formatCompactCurrency,
  getNumberFormat,
  getCompactSymbol,
  configureCurrency,
  getCurrencySymbol,
};
